/* eslint-disable complexity */
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import { isToday, parseISO, sub } from 'date-fns';
import { IntlShape } from 'react-intl';

import { AccountCategory, COAAccountNode, COACategoryNode } from '@futrli-sdk/predict';
import { UpdateCOAStructureInput } from '@futrli/views/mutations/chartOfAccounts';

import {
  BALANCE_SHEET_CATEGORIES,
  COADisplayAccount,
  COAUpdateCategory,
  ChartOfAccountsDisplay,
  P_AND_L_CATEGORIES,
  ROOT
} from './types';
import { normalizeStr } from '../utils/text';
import { TOTALS_CODE } from '../forecast/prediction-builder/types';

/**
 * Returns tree data structure for displaying chart of accounts data
 * @param coaCategories initial list of chart of account categories with children
 * @returns COADisplayAccount tree
 */
export const getDisplayAccountsTree = (coaCategories: (COACategoryNode | COAAccountNode)[]): COADisplayAccount => {
  let accountsArray: COADisplayAccount[];

  if (coaCategories.length > 0 && coaCategories[0].type === 'category') {
    const coaCategoriesChildren = flatten((coaCategories as COACategoryNode[]).map(c => c.children));
    accountsArray = getDisplayAccountsWithChildrenArr(coaCategoriesChildren as COADisplayAccount[]);
  } else {
    accountsArray = (coaCategories as COAAccountNode[]).map(account => ({
      ...account,
      children: []
    })) as COADisplayAccount[];
  }

  return {
    id: ROOT,
    name: ROOT,
    children: accountsArray,
    type: ROOT,
    code: ROOT,
    category: ROOT,
    recategorised_from: null
  };
};

/**
 * Adding missing children field to received accounts
 * @param accounts list of COADisplayAccount accounts
 * @returns list of COADisplayAccount accounts
 */
export const getDisplayAccountsWithChildrenArr = (accounts: COADisplayAccount[]): COADisplayAccount[] => {
  const accountsCopy = cloneDeep(accounts);

  const dfs = (node: COADisplayAccount) => {
    if (node?.children) {
      for (const child of node.children) {
        dfs(child);
      }
    } else {
      node.children = [];
    }
  };

  for (const cat of accountsCopy) {
    dfs(cat);
  }

  return accountsCopy;
};

/**
 * Transforms current displayed accounts to data for submitting to server
 * @param displayAccounts current draft accounts that are displayed on the screen
 * @returns Updated COA categories which can be submitted to backend
 */
export const getCOAFullStructure = (displayAccounts: COADisplayAccount[]) => {
  const coaCategories: COAUpdateCategory[] = [];
  const groupedTree = groupBy(displayAccounts, 'category');
  Object.keys(groupedTree).forEach(c => {
    coaCategories.push({ type: 'category', children: groupedTree[c], category: c });
  });
  return coaCategories;
};

/**
 * Recursive function for getting list data in flatten array
 * @param ar initial array of nested data
 * @param depth children depth, we need depth so we can create nested structure in chart of accounts list views
 */
export const getFlattenTreeData = (ar?: COADisplayAccount[], depth = 0, parentId?: string): COADisplayAccount[] => {
  if (!ar) {
    return [];
  }
  const newArr = [];
  for (const a of ar) {
    if (a.children.length > 0) {
      const newData = getFlattenTreeData(a.children, depth + 1, a.id);
      newArr.push([
        {
          id: a.id,
          code: a.code,
          name: a.name,
          children: a.children,
          category: a.category,
          type: a.type,
          days_to_pay: a.days_to_pay,
          year_to_date: a.year_to_date,
          switch_rule_category: a.switch_rule_category,
          depth,
          parentId,
          recategorised_from: a.recategorised_from
        },
        ...newData
      ]);
    } else {
      newArr.push({
        id: a.id,
        code: a.code,
        name: a.name,
        children: a.children,
        category: a.category,
        type: a.type,
        days_to_pay: a.days_to_pay,
        year_to_date: a.year_to_date,
        switch_rule_category: a.switch_rule_category,
        depth,
        parentId,
        recategorised_from: a.recategorised_from
      });
    }
  }
  return flatten(newArr);
};

/**
 * Will return an array of all of the child Ids for the given child node array
 * @param node - the child nodes to get all child IDs for
 * @param ids - Used when calling this function recursively. You should not pass data here.
 */
export const getChildIds = (children: COADisplayAccount[], ids: string[] = []) => {
  children.forEach(node => {
    ids.push(node.id);
    getChildIds(node.children, ids);
  });

  return ids;
};

/**
 * Find specific node in tree
 * @param tree accounts tree
 * @param targetValueId target node id
 * @returns
 */
export const findNode = (targetValueId: string, tree?: COADisplayAccount): COADisplayAccount | null => {
  if (!tree) {
    return null;
  }
  if (tree.id === targetValueId) {
    return tree;
  }

  for (const child of tree.children) {
    const foundNode = findNode(targetValueId, child);
    if (foundNode) {
      return foundNode;
    }
  }

  return null;
};

/**
 * Finds parent node of specific child
 * @param tree accounts tree
 * @param targetValueId target node id
 * @returns
 */
export const findParentNode = (tree: COADisplayAccount, targetValueId: string): COADisplayAccount | null => {
  if (tree.children.some(child => child.id === targetValueId)) {
    return tree;
  }

  for (const child of tree.children) {
    const parentNode = findParentNode(child, targetValueId);
    if (parentNode) {
      return parentNode;
    }
  }

  return null;
};

/**
 * Finds specific node and removes it from tree
 * @param tree accounts tree
 * @param targetValueId target node id
 * @returns
 */
export const findAndRemoveNode = (targetValueId: string, tree?: COADisplayAccount) => {
  const parent = tree ? findParentNode(tree, targetValueId) : null;

  if (parent) {
    const index = parent.children.findIndex((child: COADisplayAccount) => child.id === targetValueId);
    if (index !== -1) {
      parent.children.splice(index, 1);
    }
  }

  return tree;
};

/**
 * Adds node as child of specific parent
 * @param tree accounts tree
 * @param targetValueId target node id
 * @param nodeToAdd node that is going to be add as child of specific parent
 * @param dragIndex index in data list from which dragging process started
 * @param targetIndex index in data list on which dragging process ended
 * @param accountsList list of displayed accounts
 * @returns updated tree
 */
export const findAndAddNode = (
  tree: COADisplayAccount,
  targetValueId: string,
  nodeToAdd: COADisplayAccount,
  dragIndex: number,
  targetIndex: number,
  accountsList: COADisplayAccount[],
  nodeIdsWithParentIds: Map<string, string[]>
) => {
  const targetNode = findNode(targetValueId, tree);
  // If target node is group, we just need to add new node as
  // child of target group node
  if (targetNode?.type === 'group' && nodeToAdd.type === 'account') {
    targetNode.children.unshift(nodeToAdd);
    return tree;
  }

  // Getting parent node of node where we dropped dragged item
  const parent = findParentNode(tree, targetValueId);
  if (parent) {
    const parentIndex = accountsList.findIndex(a => a.id === parent.id);

    if (parentIndex < 0) {
      // we dragged to the root level
      const targetValueIndex = parent.children.findIndex(acc => acc.id === targetValueId);
      parent.children.splice(targetIndex > dragIndex ? targetValueIndex + 1 : targetValueIndex, 0, nodeToAdd);
    } else {
      // const targetNodeParentId = accountsList.find(a => a.id === targetNode?.id)?.parentId;
      // const nodeToAddParentId = accountsList.find(a => a.id === nodeToAdd.id)?.parentId;
      const isGroup = nodeToAdd.type === 'group';
      const ids = getChildIds(nodeToAdd.children);
      let indexToAdd = 0;
      if (targetIndex > parentIndex) {
        indexToAdd = targetIndex - parentIndex - 1;
      } else {
        indexToAdd = parentIndex - targetIndex - 1;
      }

      // if (targetIndex > dragIndex && targetNodeParentId !== nodeToAddParentId) {
      //   indexToAdd += 1;
      // }

      // if (targetNodeParentId === nodeToAddParentId) {
      //   if (targetIndex > dragIndex && isGroup) {
      //     indexToAdd -= ids.length;
      //   }
      // }

      const targetNodeParents = targetNode ? nodeIdsWithParentIds.get(targetNode.id) : [];
      const nodeToAddParents = nodeIdsWithParentIds.get(nodeToAdd.id);
      const hasAtLeastOneEqualParent = targetNodeParents?.some(id => nodeToAddParents?.includes(id));

      if (hasAtLeastOneEqualParent) {
        if (targetIndex > dragIndex && isGroup) {
          indexToAdd -= ids.length;
        }
      }
      let childrenNum = 0;
      const targetValueIndexInParent = parent.children.findIndex(c => c.id === targetValueId);
      const groupSiblings = parent.children.slice(0, targetValueIndexInParent).filter(c => c.type === 'group');
      for (const g of groupSiblings) {
        childrenNum = childrenNum + getChildIds(g.children).length;
      }

      indexToAdd -= childrenNum;
      parent.children.splice(indexToAdd, 0, nodeToAdd);
    }
  }

  return tree;
};

/**
 * Searches tree and finds node which name matches searched term. It will also
 * preserve all node's parents
 * @param root tree that is being searched
 * @param searchTerm search term that user entered
 * @returns Array of MockedAccountWithDepth arrays
 */
export const findNodeAndPreserveParents = (root: COADisplayAccount, searchTerm: string) => {
  const results: COADisplayAccount[][] = [];

  const dfs = (node: COADisplayAccount, path: COADisplayAccount[] = []) => {
    const currentPath = [...path, { ...node, depth: path.length - 1 }];

    if (normalizeStr(node.name).includes(normalizeStr(searchTerm))) {
      results.push(currentPath);
    }

    for (const child of node.children) {
      dfs(child, currentPath);
    }
  };

  dfs(root);
  return results;
};

/**
 * Removes accounts with same id
 * @param accountList list of accounts
 * @returns list of unique accounts
 */
export const removeDuplicateAccounts = (accountList: COADisplayAccount[]) => {
  const accountsArrayWithoutDuplicates = accountList.reduce((acc: COADisplayAccount[], obj: COADisplayAccount) => {
    const existingObj = acc.find(item => item.id === obj.id);
    if (!existingObj) {
      acc.push(obj);
    }
    return acc;
  }, []);

  return accountsArrayWithoutDuplicates;
};

/**
 * Searches tree and returning exact list of data that needs to be displayed
 * after entering search term
 * @param accountsTree Tree that is being searched
 * @param searchTerm search term that user entered
 * @returns Array of MockedAccountWithDepth elements
 */
export const getFilteredAccounts = (searchTerm: string, accountsTree?: COADisplayAccount) => {
  if (!accountsTree) {
    return [];
  }
  const searchedData: COADisplayAccount[][] = findNodeAndPreserveParents(accountsTree, searchTerm);
  // Removing ROOT from result
  const currentAccountsArray = flatten(searchedData).filter(d => d.id !== ROOT);
  // Removing duplicates from array
  const accountsArrayWithoutDuplicates = removeDuplicateAccounts(currentAccountsArray);

  return accountsArrayWithoutDuplicates;
};

/**
 * Grouping accounts by category and sorting them in specific order
 * @param accounts all fetched accounts
 * @returns
 */
export const getParsedAccounts = (
  accounts: ChartOfAccountsDisplay[],
  isBalanceSheet: boolean,
  accountCategoryNames: Record<AccountCategory, string>
) => {
  const groupedAccounts = groupBy(accounts, 'category');

  const accountsWithCategory: ChartOfAccountsDisplay[] = [];

  const accountsCategoriesToDisplay = isBalanceSheet ? BALANCE_SHEET_CATEGORIES : P_AND_L_CATEGORIES;

  accountsCategoriesToDisplay
    .filter(c => c !== AccountCategory.equity)
    .forEach(accountCategory => {
      const accountsInGroup = groupedAccounts[accountCategory];
      if (accountsInGroup) {
        accountsWithCategory.push({
          code: TOTALS_CODE,
          name: accountCategoryNames[accountCategory],
          category: accountCategory,
          id: TOTALS_CODE
        } as ChartOfAccountsDisplay);
        accountsWithCategory.push(...accountsInGroup);
      }
    });

  return accountsWithCategory;
};

/**
 * Returns map where key is node id and value is array of node's parents
 * @param root Tree root
 * @returns Map<string, string[]>
 */
export function getAllParentsMap(root: COADisplayAccount | undefined): Map<string, string[]> {
  const parentsMap: Map<string, string[]> = new Map();

  function traverse(node: COADisplayAccount | undefined, ancestors: string[] = []) {
    if (node) {
      const currentAncestors = [...ancestors, node.id];
      parentsMap.set(node.id, currentAncestors);

      if (node.children) {
        for (const child of node.children) {
          traverse(child, currentAncestors);
        }
      }
    }
  }

  traverse(root);

  const mapWithoutRoot: Map<string, string[]> = new Map();
  parentsMap.forEach((value: string[], key: string) => {
    const updatedValue = value.filter(v => v !== ROOT && v !== key);
    mapWithoutRoot.set(key, updatedValue);
  });

  return mapWithoutRoot;
}

/**
 * Returns formatted date string for year to date based on sync status modified date
 * @param intl IntlShape from react-intl
 * @param syncStatusModified sync status modified date string
 * @returns formatted date string
 */
export const getFormattedYearToDate = (intl: IntlShape, syncStatusModified?: string) => {
  let dateObject = parseISO(syncStatusModified || '');
  const isDateToday = isToday(dateObject);
  if (isDateToday) {
    dateObject = sub(dateObject, { days: 1 });
  }

  return intl.formatDate(dateObject, {
    day: 'numeric',
    month: 'short'
  });
};

/**
 * Returns the account name with or without the account code based on the displayAccountCodes flag
 * @param account COADisplayAccount object
 * @param displayAccountCodes boolean flag to determine if account codes should be displayed
 * @returns formatted account name string
 */
export const getAccountName = (account: COADisplayAccount, displayAccountCodes?: boolean) =>
  displayAccountCodes ? `${account.code} ${account.name}` : account.name;

/**
 * Generates the fully formatted API payload for updating the Chart of Accounts structure.
 * This utility deep clones the active tree, relocates the selected accounts into the target group,
 * handles both Create and Edit mutations, and compiles both the Profit & Loss and Balance Sheet
 * trees into the final `UpdateCOAStructureInput` request format.
 *
 * @param {string} groupId - The unique identifier of the group being created or edited.
 * @param {string} groupName - The new or updated name of the group.
 * @param {string} category - The category the group belongs to (e.g., "Revenue", "Current Assets").
 * @param {boolean} isBalanceSheet - Flag indicating which tree is being actively modified (true for Balance Sheet, false for P&L).
 * @param {COADisplayAccount} pnlTree - The cached Profit & Loss tree from the View Controller.
 * @param {COADisplayAccount} bsTree - The cached Balance Sheet tree from the View Controller.
 * @param {COADisplayAccount[]} selectedAccounts - An array of account nodes that should be moved inside this group.
 * @param {boolean} isEditMode - If true, updates an existing group node in place. If false, pushes a new group node to the tree.
 * @returns {UpdateCOAStructureInput} The fully assembled payload containing both `profit_and_loss` and `balance_sheet` arrays ready for the API.
 */
export const buildGroupStructurePayload = (
  groupId: string,
  groupName: string,
  category: string,
  isBalanceSheet: boolean,
  pnlTree: COADisplayAccount,
  bsTree: COADisplayAccount,
  selectedAccounts: COADisplayAccount[],
  isEditMode: boolean
): UpdateCOAStructureInput => {
  const draftTreeRoot = cloneDeep(isBalanceSheet ? bsTree : pnlTree);

  const accountsToMove = selectedAccounts
    .map(acc => findNode(acc.id, draftTreeRoot))
    .filter(Boolean) as COADisplayAccount[];

  selectedAccounts.forEach(acc => {
    findAndRemoveNode(acc.id, draftTreeRoot);
  });

  if (isEditMode) {
    const groupNode = findNode(groupId, draftTreeRoot);
    if (groupNode) {
      Object.assign(groupNode, {
        name: groupName,
        children: accountsToMove
      });
    }
  } else {
    const newGroupNode: COADisplayAccount = {
      id: groupId,
      name: groupName,
      category,
      type: 'group',
      code: '',
      children: accountsToMove,
      recategorised_from: null
    };
    draftTreeRoot.children.push(newGroupNode);
  }

  return {
    profit_and_loss: isBalanceSheet
      ? getCOAFullStructure(pnlTree.children)
      : getCOAFullStructure(draftTreeRoot.children),
    balance_sheet: isBalanceSheet ? getCOAFullStructure(draftTreeRoot.children) : getCOAFullStructure(bsTree.children)
  };
};
